import React from 'react';
import { useLocation } from 'react-router';
import { Box, Drawer, Fab, useMediaQuery, useTheme } from '@mui/material';
import { Menu } from '@mui/icons-material';
import DrawerHeader from './DrawerHeader';
import DrawerNavItems from './DrawerNavItems';
import DrawerFooter from './DrawerFooter';

import type { Theme } from '@mui/material/styles';

export const DRAWER_WIDTH = 240;
export const COLLAPSED_DRAWER_WIDTH = 64;

const getDrawerStyles = (theme: Theme, isOpen: boolean, isMobile: boolean) => ({
    width: isMobile ? DRAWER_WIDTH : isOpen ? DRAWER_WIDTH : COLLAPSED_DRAWER_WIDTH,
    flexShrink: 0,
    boxSizing: 'border-box' as const,
    transition: theme.transitions.create('width', {
        easing: isOpen ? theme.transitions.easing.easeOut : theme.transitions.easing.easeInOut,
        duration: isOpen ? theme.transitions.duration.enteringScreen : theme.transitions.duration.leavingScreen,
    }),
    '& .MuiDrawer-paper': {
        width: isMobile ? DRAWER_WIDTH : isOpen ? DRAWER_WIDTH : COLLAPSED_DRAWER_WIDTH,
        boxSizing: 'border-box' as const,
        backgroundColor: theme.palette.secondary.dark,
        color: theme.palette.primary.contrastText,
        borderRight: 'none',
        whiteSpace: 'nowrap',
        transition: theme.transitions.create(['width', 'margin'], {
            easing: isOpen ? theme.transitions.easing.easeOut : theme.transitions.easing.easeInOut,
            duration: isOpen ? theme.transitions.duration.enteringScreen : theme.transitions.duration.leavingScreen,
        }),
        overflowX: 'hidden',
    },
});

const AppNavDrawer: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [isOpen, setIsOpen] = React.useState(!isMobile);
    const location = useLocation();
    const currentPath = location.pathname.toLowerCase().split('/').filter(Boolean)[0];

    React.useEffect(() => {
        setIsOpen(!isMobile);
    }, [isMobile]);

    const handleToggle = () => setIsOpen(!isOpen);

    const drawerStyles = getDrawerStyles(theme, isOpen, isMobile);

    return (
        <Box sx={{ display: 'flex' }}>
            <Drawer
                variant={isMobile ? 'temporary' : 'permanent'}
                open={isMobile ? isOpen : true}
                onClose={isMobile ? handleToggle : undefined}
                ModalProps={isMobile ? { keepMounted: true } : undefined}
                sx={drawerStyles}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <DrawerHeader isOpen={isOpen} />
                    <DrawerNavItems isOpen={isOpen} currentPath={currentPath} isMobile={isMobile} handleToggle={handleToggle} />
                    <DrawerFooter isOpen={isOpen} handleToggle={handleToggle} />
                </Box>
            </Drawer>
            {isMobile && !isOpen && (
                <Fab
                    color="primary"
                    size="small"
                    onClick={handleToggle}
                    sx={{
                        position: 'fixed',
                        top: 16,
                        right: 16,
                        zIndex: theme.zIndex.drawer + 1,
                    }}
                >
                    <Menu />
                </Fab>
            )}
        </Box>
    );
};

export default AppNavDrawer;
